/**
 * Landing-page attribution: captured on page load by unlisted landing pages
 * (e.g., /mugenvip) and attached to the registration payload so every user
 * record created from that page carries its source tags.
 *
 * Registrations are always tagged with the landing page itself as
 * utm_source (e.g., "mugenvip", "start"); any inbound ad utm_source is
 * preserved separately in utm_source_captured, and utm_medium/utm_campaign
 * pass through untouched.
 *
 * Referral partner pages are FIRST-TOUCH WINS: referral fees are owed under a
 * signed agreement, so once a partner capture is stored it is never overwritten
 * by a later /start, /webinar or other capture inside its 30-day window.
 * Non-referral pages keep last-touch behaviour.
 *
 * The capture is written to localStorage AND a first-party cookie, and a
 * ?ref=<page> parameter on any URL re-captures the partner, so a fresh tab,
 * a cleared localStorage or a shared link still lands attributed.
 */
const KEY = "w3rc_landing_attribution";
const COOKIE = "w3rc_attr";
const WINDOW_DAYS = 30;
const WINDOW_MS = WINDOW_DAYS * 24 * 60 * 60 * 1000;

// Landing pages that represent a referral partner. Keep in sync with
// REFERRAL_PARTNERS in backend/referral.py.
export const REFERRAL_PAGES = ["mugenvip"];

export const isReferralPage = (page) => REFERRAL_PAGES.includes((page || "").toLowerCase());

const readCookie = () => {
  try {
    const hit = document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${COOKIE}=`));
    return hit ? JSON.parse(decodeURIComponent(hit.slice(COOKIE.length + 1))) : null;
  } catch {
    return null;
  }
};

const writeCookie = (data) => {
  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie =
      `${COOKIE}=${encodeURIComponent(JSON.stringify(data))}` +
      `; path=/; max-age=${WINDOW_DAYS * 24 * 60 * 60}; SameSite=Lax${secure}`;
  } catch {
    /* cookies unavailable: localStorage still carries the capture */
  }
};

const clearStored = () => {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* storage unavailable */
  }
  try {
    document.cookie = `${COOKIE}=; path=/; max-age=0; SameSite=Lax`;
  } catch {
    /* cookies unavailable */
  }
};

const persist = (data) => {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(data));
  } catch {
    /* storage unavailable: the cookie below is the fallback */
  }
  writeCookie(data);
};

export function captureLandingAttribution(page) {
  try {
    const params = new URLSearchParams(window.location.search);
    const captured = params.get("utm_source") || "";
    const data = {
      signup_page: page,
      utm_source: page,
      utm_source_captured: captured,
      utm_medium: params.get("utm_medium") || "",
      utm_campaign: params.get("utm_campaign") || "",
      captured_at: new Date().toISOString(),
    };
    // First-touch wins for referral partners: a live partner capture outranks
    // every later capture, including a second visit to the partner page (which
    // would otherwise keep pushing the 30-day window out indefinitely).
    const existing = getLandingAttribution();
    if (existing && isReferralPage(existing.signup_page)) return;
    persist(data);
  } catch {
    /* storage unavailable: attribution is best-effort */
  }
}

/**
 * ?ref=<page> on any URL, so a partner can link anywhere on the site and the
 * visit is still attributed. Only known referral pages are honoured; an
 * arbitrary ?ref= value is ignored rather than trusted.
 */
export function captureRefParam() {
  try {
    const ref = (new URLSearchParams(window.location.search).get("ref") || "").toLowerCase();
    if (ref && isReferralPage(ref)) captureLandingAttribution(ref);
  } catch {
    /* storage unavailable */
  }
}

export function getLandingAttribution() {
  try {
    let data = null;
    try {
      const raw = window.localStorage.getItem(KEY);
      if (raw) data = JSON.parse(raw);
    } catch {
      /* storage unavailable: fall through to the cookie */
    }
    if (!data) data = readCookie();
    if (!data) return null;
    // Attribution window: 30 days. A registration long after the landing-page
    // visit is organic, not attributable to the page.
    const capturedAt = Date.parse(data.captured_at || "");
    if (!capturedAt || Date.now() - capturedAt > WINDOW_MS) {
      clearStored();
      return null;
    }
    // localStorage may have been cleared while the cookie survived (or the
    // other way round). Re-persist so both carry the live capture.
    persist(data);
    return data;
  } catch {
    return null;
  }
}
